"use client";
import * as React from "react";
import { Field, Input, Box, HStack } from "@chakra-ui/react";
import useControllable from "hooks/useControllable/useControllable";

export interface TextFieldProps {
  id: string;
  label?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  maxLength?: number;
  allowedChars?: "numeric" | "alpha" | "alphanumeric" | RegExp;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

function filterByAllowedChars(
  value: string,
  allowed?: TextFieldProps["allowedChars"],
) {
  if (!allowed) return value;
  if (allowed === "numeric") return value.replace(/[^0-9]/g, "");
  if (allowed === "alpha") return value.replace(/[^a-zA-Z]/g, "");
  if (allowed === "alphanumeric") return value.replace(/[^0-9a-zA-Z]/g, "");
  if (allowed instanceof RegExp)
    return value
      .split("")
      .filter((ch) => allowed.test(ch))
      .join("");
  return value;
}

const TextField: React.FC<TextFieldProps> = (props) => {
  const {
    id,
    label,
    placeholder,
    required,
    helperText,
    error,
    startElement,
    endElement,
    maxLength,
    allowedChars,
    onBlur,
  } = props;

  const [value, setValue] = useControllable<string>({
    value: props.value,
    defaultValue: props.defaultValue ?? "",
    onChange: props.onChange,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let next = e.target.value ?? "";
    if (allowedChars) {
      next = filterByAllowedChars(next, allowedChars);
    }
    if (typeof maxLength === "number") {
      next = next.slice(0, maxLength);
    }
    setValue(next);
  };

  return (
    <Field.Root invalid={!!error} required={!!required}>
      {label && (
        <Field.Label>
          {label}
          {required && <Field.RequiredIndicator />}
        </Field.Label>
      )}

      <Box w="full">
        {startElement || endElement ? (
          <HStack>
            {startElement && <Box>{startElement}</Box>}
            <Input
              id={id}
              name={id}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              placeholder={placeholder}
              width="full"
            />
            {endElement && <Box>{endElement}</Box>}
          </HStack>
        ) : (
          <Input
            id={id}
            name={id}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            width="full"
          />
        )}
      </Box>

      {error ? (
        <Field.ErrorText>{error}</Field.ErrorText>
      ) : helperText ? (
        <Field.HelperText>{helperText}</Field.HelperText>
      ) : null}
    </Field.Root>
  );
};

export default TextField;
